import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchOccurrenceStatus, fetchOrder, omitSecrets } from "../api/backend.js";
import Page from "../components/layout/Page.jsx";
import SidePanel from "../components/SidePanel.jsx";
import { Alert, Button, Card, Field, Input } from "../components/ui.jsx";
import { useOccurrence } from "../context/OccurrenceContext.jsx";
import { useIframeHeight } from "../hooks/useIframeHeight.js";
import { clearFlow, EMAIL_PATTERN, normalizeOrderName } from "../utils/session.js";
import styles from "./Initial.module.css";

const Initial = () => {
  const { shop } = useParams();
  const { store, storeStatus, setOrder, setCustomerEmail, setResult } = useOccurrence();
  // Vêm do bloco do tema: link do e-mail (?name=&email=) ou cliente logado na loja.
  const query = new URLSearchParams(window.location.search);
  const [name, setName] = useState(query.get("name") || "");
  const [email, setEmail] = useState(query.get("email") || query.get("prefillEmail") || "");
  const [fieldErrors, setFieldErrors] = useState({});
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useIframeHeight();


  const validate = () => {
    const next = {};
    if (!normalizeOrderName(name)) {
      next.name = "Informe o número do pedido.";
    }
    if (!email.trim()) {
      next.email = "Informe o e-mail da compra.";
    } else if (!EMAIL_PATTERN.test(email.trim())) {
      next.email = "E-mail inválido.";
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleFormSubmit = async (event) => {
    event?.preventDefault();
    setMessage("");
    if (!validate()) {
      return;
    }

    const orderName = normalizeOrderName(name);
    const trimmedEmail = email.trim();
    // Nova consulta: descarta o que ficou de outro pedido na sessão.
    clearFlow();
    setResult(undefined);
    setOrder(undefined);
    setLoading(true);
    try {
      // 1º a consulta rápida (só banco): se já existe solicitação, vai direto ao status
      // sem esperar a busca completa do pedido na Shopify.
      const existingFirst = await fetchOccurrenceStatus(shop, orderName, trimmedEmail).catch(() => null);
      const terminal = ["Recusado", "Cancelado"].includes(existingFirst?.statusOccurrence);
      if (existingFirst && !terminal) {
        setCustomerEmail(trimmedEmail);
        setResult(existingFirst);
        navigate(`/${shop}/post/${orderName}/${encodeURIComponent(trimmedEmail)}`);
        return;
      }
      const order = await fetchOrder(shop, orderName, trimmedEmail);
      setOrder(order);
      setCustomerEmail(trimmedEmail);
      navigate(`/${shop}/products/${orderName}/${encodeURIComponent(trimmedEmail)}`);
    } catch (error) {
      const status = error?.response?.status;
      const apiMessage = error?.response?.data?.message;
      if (status === 422) {
        setCustomerEmail(trimmedEmail);
        const existing = error?.response?.data?.occurrence
          ? omitSecrets(error.response.data.occurrence)
          : await fetchOccurrenceStatus(shop, orderName, trimmedEmail);
        if (existing) {
          setResult(existing);
          navigate(`/${shop}/post/${orderName}/${encodeURIComponent(trimmedEmail)}`);
          return;
        }
        setMessage(apiMessage || "Já existe uma solicitação em andamento para este pedido.");
      } else if (status === 409) {
        setMessage(apiMessage || "Este pedido está fora do prazo de troca ou devolução.");
      } else if (status === 404) {
        setMessage(apiMessage || "Não encontramos um pedido com esses dados. Confira o número e o e-mail.");
      } else {
        setMessage(apiMessage || "Não foi possível buscar o pedido. Tente de novo em instantes.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page
      aside={
        <SidePanel
          title="Como funciona"
          steps={[
            { title: "Encontre o pedido", text: "Use o número do pedido e o e-mail da compra." },
            { title: "Escolha os itens e o motivo", text: "Troque por outro tamanho/cor, peça reembolso ou crédito." },
            { title: "Envie pelos Correios", text: "Você recebe um código de postagem: é só levar o produto à agência, sem pagar frete." },
            { title: "Acompanhe por aqui", text: "Avisamos por e-mail a cada etapa." },
          ]}
        />
      }
      title="Trocas e devoluções"
      subtitle={store?.name ? `Pedido da ${store.name}` : "Identifique seu pedido para continuar"}
      step="identify"
    >
      <Card>
        <form className={styles.form} onSubmit={handleFormSubmit} noValidate>
          {!store?.initialMessages ? (
            <div className={styles.texts}>
              <h2>Buscar pedido</h2>
              <p>Informe o número do pedido e o e-mail usado na compra. É o que usamos para localizar os itens.</p>
            </div>
          ) : null}

          {storeStatus === "error" ? (
            <Alert tone="info">Não carregamos a vitrine agora, mas você ainda pode buscar o pedido.</Alert>
          ) : null}

          <Field
            id="order-number"
            label="Número do pedido"
            hint="Pode ser 1042 ou #1042"
            error={fieldErrors.name}
          >
            <Input
              id="order-number"
              name="order"
              inputMode="numeric"
              autoComplete="off"
              placeholder="Ex.: 1042"
              value={name}
              error={fieldErrors.name}
              hint="Pode ser 1042 ou #1042"
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <Field
            id="order-email"
            label="E-mail da compra"
            error={fieldErrors.email}
          >
            <Input
              id="order-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="voce@email.com"
              value={email}
              error={fieldErrors.email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          {message ? <Alert>{message}</Alert> : null}

          <Button type="submit" loading={loading}>
            Buscar pedido
          </Button>
        </form>
      </Card>
    </Page>
  );
};

export default Initial;
